<template>
	<div
		class="input-check"
		:class="[
			{ 'input-check--has-eyebrow': eyebrow || alignForEyebrow },
			{ 'light-theme': lightFontTheme }
		]"
	>
		<input
			v-if="!displayLockIcon() && !anchorLink"
			type="checkbox"
			:id="id"
			class="input-check__input"
			@change="emitUpdateSelectedItemsEvent"
			:checked="isChecked"
		/>

		<label
			class="input-check__label"
			:class="[{ 'anchor-link': anchorLink }, { locked: displayLockIcon() }]"
			:tabindex="displayLockIcon() ? '0' : '-1'"
			:for="id"
			@keydown.enter="onLockedItemClicked"
			@click="onLockedItemClicked"
		>
			<div v-if="displayLockIcon() && !anchorLink" class="lock-wrapper">
				<Icon ariaLabel="locked" type="lock" />
			</div>
			<p v-if="eyebrow" class="eyebrow">{{ eyebrow }}</p>
			<span
				v-if="displayTitle && !anchorLink"
				class="input-check__label-title p2"
				:class="{ 'input-check__label-title--has-description': description }"
				v-html="displayTitle"
			/>
			<a
				v-if="displayTitle && anchorLink"
				:href="link"
				:class="['anchor', { 'anchor-link--has-description': description }]"
				:target="anchorLinkTargetBlank ? '_blank' : '_self'"
				:rel="anchorLinkTargetBlank ? 'noopener noreferrer' : null"
				:title="anchorLinkTitle"
				v-html="displayTitle"
			/>
			<span
				v-if="description"
				class="input-check__label-description p3"
				v-html="description"
			/>
			<span
				v-if="displayLockIcon()"
				role="alert"
				class="non-register"
				:class="{ hidden: isHidden }"
				v-html="lockMessage"
			/>
		</label>

		<template v-if="!anchorLink">
			<a
				v-if="!hideDownloadIcon && link && !displayLockIcon()"
				class="input-check__download-link"
				:href="downloadIconLink"
				target="_blank"
				rel="noopener noreferrer"
				@click="trackItemDownload"
				:title="buttonTitle"
			>
				<span class="visually-hidden">Download {{ displayTitle }}</span>
				<span class="icon icon-download" aria-hidden="true" />
			</a>
			<a
				v-else-if="!hideDownloadIcon && link"
				class="grey input-check__download-link"
			>
				<span class="visually-hidden">
					Download {{ displayTitle }} (Locked - Sign in to download)</span
				>
				<span class="icon icon-download" aria-hidden="true" />
			</a>
		</template>
	</div>
</template>

<script>
import fileDownloaderService from "../services/fileDownloader.service.js";
import { adobeLaunch } from "vg-fas-adobe-launch";
import { Icon } from "@vg-faset/ui/src/components/icon";
import { EventBus } from "@vg-faset/core";

export default {
	name: "DownloadMultiSelectItem",
	components: {
		Icon
	},
	data() {
		return {
			isHidden: true
		};
	},
	props: {
		id: {
			type: String
		},
		anchorLink: {
			type: Boolean,
			default: false
		},
		anchorLinkTargetBlank: {
			type: Boolean,
			default: true
		},
		anchorLinkTitle: {
			type: String,
			default: "Opens a web page"
		},
		eyebrow: {
			type: String
		},
		link: {
			type: String
		},
		forceAsAbsolutePath: {
			type: Boolean,
			default: false
		},
		fundId: {
			type: String
		},
		litId: {
			type: String
		},
		hideDownloadIcon: {
			type: Boolean,
			default: false
		},
		lockContent: {
			type: Boolean
		},
		lockItem: {
			type: Boolean,
			default: false
		},
		lockMessage: {
			type: String
		},
		isChecked: {
			type: Boolean
		},
		displayTitle: {
			type: String
		},
		downloadTitle: {
			type: String
		},
		description: {
			type: String
		},
		alignForEyebrow: {
			type: Boolean,
			default: false
		},
		lightFontTheme: {
			type: Boolean,
			default: false
		}
	},
	methods: {
		emitUpdateSelectedItemsEvent(event) {
			this.$emit("update:selectedItems", {
				selected: event.target.checked,
				id: this.id,
				downloadTitle: this.downloadTitle,
				displayTitle: this.displayTitle,
				link: this.link,
				forceAsAbsolutePath: this.forceAsAbsolutePath,
				fundId: this.fundId,
				litId: this.litId
			});
		},
		trackItemDownload() {
			this.$emit("DownloadButtonClicked", this.id);

			const fileName = this.link.substring(
				this.link.lastIndexOf("/") + 1,
				this.link.lastIndexOf(".")
			);
			const fileExtension = this.link.split(".").pop();

			const litIdTrack = this.litId ? `lit:${this.litId}` : null;

			adobeLaunch.trackContentDownloaded(
				`${fileExtension.toLowerCase()}:${fileName}`,
				litIdTrack,
				this.fundId
			);
		},
		displayLockIcon() {
			return this.lockItem && this.lockContent;
		},
		onLockedItemClicked() {
			if (this.displayLockIcon()) {
				this.$emit("LockedItemClicked");
			}
		}
	},
	computed: {
		downloadIconLink() {
			return this.forceAsAbsolutePath
				? this.link
				: fileDownloaderService.getRelativePath(this.link);
		},
		buttonTitle() {
			let value = "";
			if (this.link.split(".").pop().toUpperCase() === "XLSX") {
				value = "Downloads a spreadsheet";
			} else if (this.link.split(".").pop().toUpperCase() === "CSV") {
				value = "Downloads a spreadsheet";
			} else if (this.link.split(".").pop().toUpperCase() === "PDF") {
				value = "Opens in a new window";
			} else {
				value = `Opens a ${this.link.split(".").pop().toUpperCase()}`;
			}
			return value;
		}
	},
	mounted() {
		EventBus.$on("download-tool:hideDisclaimer", () => {
			this.isHidden = true;
		});
		EventBus.$on("download-tool:showDisclaimer", () => {
			this.isHidden = false;
		});
	}
};
</script>

<style lang="scss" scoped>
.input-check {
	padding: 30px 0;
	flex-wrap: nowrap;

	&__download-link {
		align-self: flex-start;
		color: $color--almost-black;
		margin-left: auto;
		padding-left: 10px;
		transform: translateY(25%);
		transition: color 0.2s ease;

		&:hover,
		&:focus {
			color: $color--red;
		}

		@include from-md {
			margin-right: 50px;
		}
	}

	.non-register {
		height: 18px;
		color: #e21c2d;
		font-family: "Univers LT Std";
		font-size: 12px;
		line-height: 18px;
	}

	.hidden {
		display: none;
	}

	.icon-lock {
		align-self: flex-start;
		min-width: 24px;
		padding-top: 3px;
		padding-left: 5px;
		margin-right: 14px;
	}

	.grey.input-check__download-link {
		color: $color--mid-gray;
	}

	input {
		align-self: flex-start;
		min-width: 24px;
		max-width: 24px;
		width: 24px;
		flex: 1;
	}

	.input-check {
		&__input {
			cursor: pointer;
		}

		&__label {
			flex: 10;
			align-self: flex-start;
			line-height: 1.4;
			margin-right: 24px;
			padding-top: 2px;
			position: relative;
			cursor: pointer;

			@include from-md {
				margin-right: 44px;
			}

			&.locked {
				padding-left: 38px;
			}

			.eyebrow {
				position: absolute;
				top: -15px;
			}

			.lock-wrapper {
				position: absolute;
				left: 0;
				cursor: pointer;
			}
		}
	}

	&--has-eyebrow {
		@include to-md {
			padding: 35px 0 13px;
		}

		@include from-md {
			margin-top: 15px;
		}
	}

	.eyebrow {
		color: $color--neutral-6;
	}

	.input-check__label-title {
		display: block;
		line-height: 1.4;

		&.input-check__label-title--has-description {
			font-weight: bold;

			@include to-md {
				font-weight: normal;

				+ .input-check__label-description {
					display: none;
				}
			}
		}
	}

	&.light-theme {
		.input-check__label-title--has-description {
			font-weight: normal;
		}

		.input-check__label-description.p3 {
			font-weight: 300;
		}
	}

	.anchor-link {
		margin: 0 50px 0 38px;

		@include from-md() {
			margin: 0 120px 0 38px;
		}

		a {
			color: $color--almost-black;

			&:hover {
				text-decoration: underline;
			}
		}

		&--has-description {
			display: block;
		}

		& .input-check__label-description {
			display: block;
		}
	}
}
</style>
